import { KeyValuePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';
import { filter } from 'rxjs/operators';
import { GmrDataService } from '../core/gmr-data.service';
import { UiStateService } from '../core/ui-state.service';
import { GmrFunction } from '../core/gmr.models';
import { buildSearchHaystack } from '../shared/function-search';
import { DecodeEntitiesPipe } from '../shared/pipes/decode-entities.pipe';

//
// 1:1 port of the filterable step1 function list (#chooseFilter) in menu.html. The always-visible
// sidebar (.menuLeft) is a separate SidebarComponent - see its file header for why splitting it
// out was necessary (it must be a true flex-sibling of .getMyReport, not nested in here).
//
@Component({
  selector: 'app-function-picker',
  standalone: true,
  imports: [FormsModule, KeyValuePipe, TranslateDirective, TranslatePipe, DecodeEntitiesPipe],
  styles: [':host { display: block; height: 100%; }'],
  templateUrl: './function-picker.component.html',
})
export class FunctionPickerComponent
{
  protected readonly data = inject(GmrDataService);
  protected readonly ui = inject(UiStateService);

  protected readonly functionsFilter = signal('');

  get functionsFilterModel(): string
  {
    return this.functionsFilter();
  }

  set functionsFilterModel(value: string)
  {
    this.functionsFilter.set(value);
  }

  //
  // keyvalue's default sort is alphabetical by key - groupBy's Map preserves the original
  // first-seen insertion order (matching AngularJS' object-keyed ng-repeat), so sorting is
  // disabled here rather than re-sorted alphabetically.
  //
  protected readonly originalOrder = () => 0;

  private readonly translate = inject(TranslateService);

  //
  // "The rendered translations may have changed." Deliberately the same three sources @ngx-translate
  // merges for its own refresh stream (ngx-translate-core.mjs, TranslateDirective) rather than a
  // hand-picked subset: a language switch, a translation table arriving or being replaced, and a
  // fallback-language change all alter what is on screen.
  //
  // Without this the haystacks would be built once, from whatever was loaded at that moment - and
  // the catalog arrives before translate.use() runs (GmrDataService applies the profile language only
  // after the user info is in), so instant() would still be answering with raw resource keys. That is
  // exactly the "itgen matches everything" behaviour this whole change removes.
  //
  private readonly translationsChanged = toSignal
  ( merge
    ( this.translate.onTranslationChange.pipe(filter(event =>
        event.lang === this.translate.getCurrentLang() || event.lang === this.translate.getFallbackLang()))
    , this.translate.onLangChange
    , this.translate.onFallbackLangChange
    )
  , { initialValue: null }
  );

  //
  // One lowercased search string per report, rebuilt when the catalog changes or the translations do,
  // never per keystroke. Keyed on the function object itself: the same instances are handed out by
  // groupedFunctions(), so identity lookup is safe and needs no separate id.
  //
  private readonly searchHaystacks = computed(() =>
  {
    this.translationsChanged();

    const haystacks = new Map<GmrFunction, string>();
    for (const functions of this.data.groupedFunctions().values())
    {
      for (const fn of functions)
      {
        haystacks.set(fn, buildSearchHaystack(fn, key => this.translate.instant(key)));
      }
    }
    return haystacks;
  });

  protected readonly filteredGroupedFunctions = computed(() =>
  {
    const term = this.functionsFilter().toLowerCase();
    if (!term) return this.data.groupedFunctions();

    const haystacks = this.searchHaystacks();
    const filtered = new Map<string, GmrFunction[]>();
    for (const [key, functions] of this.data.groupedFunctions())
    {
      const matches = functions.filter(fn => haystacks.get(fn)?.includes(term) ?? false);
      if (matches.length > 0)
      {
        filtered.set(key, matches);
      }
    }
    return filtered;
  });

  //
  // 1:1 port of closeGroupHeading/closeGroupIcon for this list. Collapse state is local to this
  // component - see SidebarComponent for why it isn't shared with the sidebar's own copy.
  //
  private readonly collapsedGroups = signal(new Set<string>());

  isGroupCollapsed(groupKey: string): boolean
  {
    return this.collapsedGroups().has(groupKey);
  }

  toggleGroupCollapse(groupKey: string): void
  {
    this.collapsedGroups.update(current =>
    {
      const next = new Set(current);
      if (next.has(groupKey)) next.delete(groupKey); else next.add(groupKey);
      return next;
    });
  }

  selectFunction(fn: GmrFunction): void
  {
    const showsParameterForm = this.data.selectFunction(fn);
    if (showsParameterForm)
    {
      this.ui.goToParameterForm(fn.cfn_preset_name);
    }
  }
}
